/**
 * ⚠️ TEMPORARY — DELETE ME.
 *
 * This module exists only to stand up the board off live data without checking in a
 * production fixture. It maps a `RepoIssuesDashboardContentResponse` list-view payload
 * (proto-JSON returned by `GET /:owner/:repo/issues`) into the board's
 * `BoardInitialPayload` contract, faking column grouping client-side by label.
 *
 * It goes away the moment the board has its own REST endpoint that returns the payload
 * shape directly. Do not extend this file with new features, new grouping strategies,
 * or new callers — anything you're tempted to add here belongs in that endpoint.
 */

import type {
  RepoIssue,
  RepoIssueAssignee,
  RepoIssueMilestone,
  RepoIssuePullRequest,
  RepoIssuePullRequestAssignee,
  RepoIssuesDashboardLabel,
  RepoIssuesDashboardResult,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {
  RepoIssuePullRequestDisplayState,
  RepoIssueState,
  RepoIssueStateReason,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'

import type {
  BoardInitialPayload,
  BoardPayloadAssignee,
  BoardPayloadCell,
  BoardPayloadColumn,
  BoardPayloadItem,
  BoardPayloadLabel,
  BoardPayloadMilestone,
  IssueStateReason,
  PullRequestState,
} from './board-payload'

/**
 * Bucket key used for items that carry no labels. Rendered as `"No label"` on the axis.
 * A literal key that can never collide with a real label name keeps the group stable
 * even if a user creates a label called `No label`.
 */
const NO_LABEL_KEY = '__no_label__'

/**
 * Maps the list-view search response into a {@link BoardInitialPayload} grouped by label.
 *
 * The board is single-axis for now (no swimlane; `rowField: null`). Every item is placed
 * in the column for each of its labels (so `countsOverlap: true`); items with no labels
 * fall into the `"No label"` bucket. Cells advertise `items.length` as their `totalCount`
 * only because paging isn't wired yet (github/views#139).
 */
export function resultsToBoardPayload(results: readonly RepoIssuesDashboardResult[]): BoardInitialPayload {
  const items = results.map(toBoardItem).filter((item): item is BoardPayloadItem => item !== null)

  const bucketsByKey = new Map<string, BoardPayloadItem[]>()
  for (const item of items) {
    const labels = item.fields.label?.value ?? []
    if (labels.length === 0) {
      appendItem(bucketsByKey, NO_LABEL_KEY, item)
      continue
    }
    for (const label of labels) {
      appendItem(bucketsByKey, label.name, item)
    }
  }

  const columns: BoardPayloadColumn[] = []
  const cells: BoardPayloadCell[] = []
  for (const [key, bucket] of bucketsByKey) {
    columns.push({key, label: key === NO_LABEL_KEY ? 'No label' : key, totalCount: bucket.length})
    cells.push({
      columnKey: key,
      rowKey: 'all',
      totalCount: bucket.length,
      hasMoreItems: false,
      itemsCursor: null,
      items: bucket,
    })
  }

  return {
    columns,
    rows: [{key: 'all', label: '', totalCount: items.length}],
    columnField: 'label',
    rowField: null,
    cells,
    hasMoreColumns: false,
    hasMoreRows: false,
    columnsTruncated: false,
    rowsTruncated: false,
    countsOverlap: true,
    columnsCursor: null,
    rowsCursor: null,
  }
}

function appendItem(buckets: Map<string, BoardPayloadItem[]>, key: string, item: BoardPayloadItem): void {
  const existing = buckets.get(key)
  if (existing) {
    existing.push(item)
  } else {
    buckets.set(key, [item])
  }
}

function toBoardItem(result: RepoIssuesDashboardResult): BoardPayloadItem | null {
  if (result.item.case === 'issue') return toIssueItem(result.item.value, result.id)
  if (result.item.case === 'pullRequest') return toPullRequestItem(result.item.value, result.id)
  return null
}

function toIssueItem(issue: RepoIssue, globalId: string): BoardPayloadItem {
  return {
    type: 'issue',
    // The proto `id` is the global id; the payload's numeric `id` isn't read by the card.
    id: 0,
    globalId,
    number: issue.number,
    title: issue.title,
    titleHtml: issue.titleHtml?.value ?? issue.title,
    permalink: issue.permalink ?? '',
    repoNameWithOwner: issue.repoNameWithOwner ?? '',
    state: issue.state === RepoIssueState.CLOSED ? 'closed' : 'open',
    stateReason: mapIssueStateReason(issue.stateReason),
    fields: {
      label: {value: issue.labels.map(readLabel)},
      milestone: {value: readMilestone(issue.milestone)},
      assignee: {value: issue.assignees.map(readIssueAssignee)},
    },
  }
}

function toPullRequestItem(pull: RepoIssuePullRequest, globalId: string): BoardPayloadItem {
  return {
    type: 'pullRequest',
    id: 0,
    globalId,
    number: pull.number,
    title: pull.title,
    titleHtml: pull.titleHtml?.value ?? pull.title,
    permalink: pull.permalink ?? '',
    repoNameWithOwner: pull.repoNameWithOwner ?? '',
    state: mapPullRequestState(pull),
    fields: {
      label: {value: pull.labels.map(readLabel)},
      milestone: {value: null},
      assignee: {value: pull.assignees.map(readPullRequestAssignee)},
    },
  }
}

function mapIssueStateReason(reason: RepoIssueStateReason): IssueStateReason | null {
  switch (reason) {
    case RepoIssueStateReason.COMPLETED:
      return 'completed'
    case RepoIssueStateReason.NOT_PLANNED:
      return 'not_planned'
    case RepoIssueStateReason.REOPENED:
      return 'reopened'
    case RepoIssueStateReason.DUPLICATE:
      return 'duplicate'
    default:
      return null
  }
}

function mapPullRequestState(pull: RepoIssuePullRequest): PullRequestState {
  // `displayState` folds draft/queued into the same enum the card renders on.
  switch (pull.displayState) {
    case RepoIssuePullRequestDisplayState.DRAFT:
      return 'draft'
    case RepoIssuePullRequestDisplayState.MERGED:
      return 'merged'
    case RepoIssuePullRequestDisplayState.CLOSED:
      return 'closed'
    case RepoIssuePullRequestDisplayState.QUEUED:
      return 'queued'
    default:
      return 'open'
  }
}

function readLabel(label: RepoIssuesDashboardLabel): BoardPayloadLabel {
  return {
    // Proto `id` is a global string; the payload numeric `id` isn't read by the card.
    id: 0,
    name: label.name,
    nameHtml: label.nameHtml?.value ?? label.name,
    color: label.color ?? '',
    description: label.description ?? null,
  }
}

function readMilestone(milestone: RepoIssueMilestone | undefined): BoardPayloadMilestone | null {
  if (!milestone) return null
  // The list-view proto only carries `title` + `url`. `state` and `dueOn` aren't wired
  // on this endpoint; default to open/null until the board endpoint delivers them.
  return {title: milestone.title, url: milestone.url, state: 'open', dueOn: null}
}

function readIssueAssignee(assignee: RepoIssueAssignee): BoardPayloadAssignee {
  return {
    login: assignee.login,
    name: assignee.name ?? null,
    avatarUrl: assignee.avatarUrl,
    isAgent: assignee.isAgent,
    isCopilot: assignee.isCopilot,
  }
}

function readPullRequestAssignee(assignee: RepoIssuePullRequestAssignee): BoardPayloadAssignee {
  // Pull-request assignee proto is a minimal `{login, avatarUrl}` — no agent flag or name.
  return {login: assignee.login, name: null, avatarUrl: assignee.avatarUrl, isAgent: false, isCopilot: false}
}
